/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Lozenge from '@atlaskit/lozenge';
import { Anchor, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

/**
 * The default Atlassian Intelligence disclaimer link. This mirrors the link used by the
 * generative AI modal footer (`FooterDisclaimer`).
 */
const DEFAULT_DISCLAIMER_URL = 'https://www.atlassian.com/trust/atlassian-intelligence';

const styles = cssMap({
	link: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
		textDecoration: 'none',
		'&:visited': {
			color: token('color.text.subtlest'),
		},
		// Disable the default link hover styling.
		'&:hover': {
			color: token('color.text.subtlest'),
			textDecoration: 'none',
		},
	},
});

/**
 * Footer displayed at the bottom of the AI suggestion card.
 *
 * Renders: [info icon] "Uses AI. Verify results." [link-out icon] [Labs lozenge]
 * where the link-out icon links to the default Atlassian Intelligence disclaimer page.
 * Both the disclaimer and Labs lozenge are centred as a group.
 */
export function SuggestionCardFooter(): JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<Inline space="space.200" alignBlock="center" alignInline="center">
			<Inline space="space.050" alignBlock="center">
				{/* The icon is decorative; the adjacent text conveys the same information. */}
				<InformationCircleIcon label="" color={token('color.icon.subtlest')} size="small" />
				<Anchor
					href={DEFAULT_DISCLAIMER_URL}
					target="_blank"
					rel="noopener noreferrer"
					xcss={styles.link}
				>
					<Inline space="space.050" alignBlock="center" as="span">
						{formatMessage(aiSuggestionsMessages.cardDisclaimerText)}
						{/* The icon is decorative here as the anchor provides the accessible label. */}
						<LinkExternalIcon label="" color="currentColor" size="small" />
					</Inline>
				</Anchor>
			</Inline>
			<Lozenge appearance="discovery">
				{formatMessage(aiSuggestionsMessages.labsLozengeLabel)}
			</Lozenge>
		</Inline>
	);
}
